/*
==============================================================================
NUPEP Cart Subscription Manager
Version: 9.0.0

Responsibilities
----------------
✓ Replace subscription schedules
✓ Verify replacement before removal
✓ Never manipulate HTML
✓ Never call Shopify directly except through CartAPI
✓ Refresh cart only once
==============================================================================
*/

"use strict";

window.NUPEP = window.NUPEP || {};

NUPEP.CartSubscriptions = {
  async replace(button) {
    const request = buildRequest(button);

    if (!request) {
      return false;
    }
    // Prevent duplicate requests
    if (button.disabled) {
      return false;
    }
    if (request.currentPlan === request.newPlan) {
      return true;
    }

    showLoading(button);

    try {
      //----------------------------------------------------------
      // Read current cart
      //----------------------------------------------------------

      const cart = await NUPEP.CartAPI.getCart();

      //----------------------------------------------------------
      // Find original subscription
      //----------------------------------------------------------

      const originalLine = findOriginalLine(cart, request);

      if (!originalLine) {
        throw new Error("Original subscription could not be located.");
      }

      //----------------------------------------------------------
      // Add replacement subscription
      //----------------------------------------------------------

      await NUPEP.CartAPI.add(
        request.variantId,
        request.quantity,
        request.newPlan,
        request.properties,
      );
      //----------------------------------------------------------
      // Read updated cart
      //----------------------------------------------------------

      const updatedCart = await NUPEP.CartAPI.getCart();

      //----------------------------------------------------------
      // Verify replacement exists
      //----------------------------------------------------------

      const replacement = verifyReplacement(updatedCart, request);

      if (!replacement) {
        throw new Error("Replacement subscription could not be verified.");
      }
      if (replacement.quantity !== request.quantity) {
        throw new Error("Replacement quantity mismatch.");
      }

      //----------------------------------------------------------
      // Remove original subscription
      //----------------------------------------------------------

      await NUPEP.CartAPI.remove(originalLine.key);

      //----------------------------------------------------------
      // Refresh framework
      //----------------------------------------------------------

      await NUPEP.Cart.refresh();

      showSuccess(button);

      return true;
    } catch (error) {
      console.error("Subscription Update Failed");
      console.error("Request:", request);
      console.error("Error:", error);

      showError(button, error.message);

      return false;
    }
  },
};

/*==============================================================================
Build Request
==============================================================================*/

function buildRequest(button) {
  const card = button.closest(".lux-cart-item");

  if (!card) {
    return null;
  }

  const selected = card.querySelector(
    '.nupep-delivery-selector input[type="radio"]:checked',
  );

  if (!selected) {
    showError(button, "Please select a delivery schedule.");
    return null;
  }

  const quantityInput = card.querySelector(".qty-input");
  const cart = NUPEP.Cart.getState();

  const original = cart.items.find((item) => item.key === button.dataset.key);

  return {
    lineKey: button.dataset.key,

    variantId: Number(button.dataset.variant),

    productId: Number(button.dataset.product),

    quantity: quantityInput ? Number(quantityInput.value) : 1,

    currentPlan: Number(button.dataset.currentPlan),

    newPlan: Number(selected.dataset.sellingPlan || selected.value),

    properties: original?.properties || {},
  };
}

/*==============================================================================
Locate Original Subscription
==============================================================================*/

function findOriginalLine(cart, request) {
  return cart.items.find(
    (item) =>
      item.key === request.lineKey &&
      Number(item.variant_id) === request.variantId &&
      item.selling_plan_allocation &&
      Number(item.selling_plan_allocation.selling_plan.id) ===
        request.currentPlan,
  );
}

/*==============================================================================
Verify Replacement
==============================================================================*/

function verifyReplacement(cart, request) {
  return cart.items.find((item) => {
    if (
      Number(item.variant_id) !== request.variantId ||
      item.key === request.lineKey ||
      !item.selling_plan_allocation ||
      Number(item.selling_plan_allocation.selling_plan.id) !== request.newPlan
    ) {
      return false;
    }

    return (
      JSON.stringify(item.properties || {}) ===
      JSON.stringify(request.properties || {})
    );
  });
}

/*==============================================================================
Button Helpers
==============================================================================*/

function showLoading(button) {
  button.disabled = true;

  button.setAttribute("aria-busy", "true");

  button.classList.add("is-loading");

  const text = button.querySelector(".button-text");

  const loader = button.querySelector(".button-loader");

  if (text) {
    text.hidden = true;
  }

  if (loader) {
    loader.hidden = false;
  }
}

function showSuccess(button) {
  button.disabled = false;
  button.removeAttribute("aria-busy");

  button.classList.remove("is-loading");

  button.classList.add("is-success");

  const text = button.querySelector(".button-text");
  const loader = button.querySelector(".button-loader");

  if (loader) loader.hidden = true;

  if (text) {
    text.hidden = false;
    text.textContent = "✓ Updated";
  }

  setTimeout(() => {
    button.classList.remove("is-success");

    if (text) {
      text.textContent = "Update Schedule";
    }
  }, 1500);
}

function showError(button, message) {
  button.disabled = false;

  button.removeAttribute("aria-busy");

  button.classList.remove("is-loading");

  const text = button.querySelector(".button-text");
  const loader = button.querySelector(".button-loader");

  if (loader) loader.hidden = true;

  if (text) {
    text.hidden = false;
    text.textContent = "Update Schedule";
  }

  console.error(message);
}
